import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import ProfileHeader from "./components/ProfileHeader";
import PersonalizeSection from "./components/PersonalizeSection";
import ImageEditor from "./components/ImageEditor";
import SellerReviewsList from "@/components/feature/SellerReviewsList";
import { EMPTY_RATING, fetchSellerRating, type RatingSummary } from "@/lib/reviews";

export default function ProfilePage() {
  const { user, profile, loading, refreshProfile } = useAuth();

  const [counts, setCounts] = useState({
    published: 0,
    sold: 0,
    favorites: 0,
  });
  const [editor, setEditor] = useState<null | "cover" | "avatar">(null);
  const [rating, setRating] = useState<RatingSummary>(EMPTY_RATING);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    async function load() {
      try {
        const [pubRes, soldRes, favRes] = await Promise.all([
          supabase
            .from("products")
            .select("id", { count: "exact", head: true })
            .eq("seller_id", user.id)
            .eq("status", "active"),
          supabase
            .from("products")
            .select("id", { count: "exact", head: true })
            .eq("seller_id", user.id)
            .eq("status", "sold"),
          supabase
            .from("favorites")
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id),
        ]);
        if (mounted) {
          setCounts({
            published: pubRes.count ?? 0,
            sold: soldRes.count ?? 0,
            favorites: favRes.count ?? 0,
          });
        }
        const summary = await fetchSellerRating(user.id);
        if (mounted) setRating(summary);
      } catch {
        // Silencioso: los contadores no son críticos.
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  if (loading || !profile) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  async function persistAvatar(url: string | null) {
    if (!user) throw new Error("No estás autenticado.");
    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: url })
      .eq("id", user.id);
    if (error) throw new Error(error.message);
    await refreshProfile();
  }

  async function persistCover(url: string | null) {
    if (!user) throw new Error("No estás autenticado.");
    const { error } = await supabase
      .from("profiles")
      .update({ cover_image: url })
      .eq("id", user.id);
    if (error) throw new Error(error.message);
    await refreshProfile();
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Barra superior */}
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 md:px-6">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-500">
            Mi perfil
          </h2>
        </div>
        {profile.username ? (
          <div className="flex items-center gap-2">
            <Link
              to="/perfil/configuracion"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-settings-3-line"></i>
              Configuración
            </Link>
            <Link
              to={`/perfil/${profile.username}`}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-eye-line"></i>
              Así te ven otros
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link
              to="/perfil/configuracion"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-settings-3-line"></i>
              Configuración
            </Link>
            <span className="hidden items-center gap-2 text-xs text-foreground-400 sm:inline-flex">
              <i className="ri-information-line"></i>
              Configurá tu username para tener perfil público
            </span>
          </div>
        )}
      </div>

      <ProfileHeader
        profile={profile}
        counts={counts}
        rating={rating}
        onChangeCover={() => setEditor("cover")}
        onChangeAvatar={() => setEditor("avatar")}
      />

      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
        <PersonalizeSection
          onChangeCover={() => setEditor("cover")}
          onChangeAvatar={() => setEditor("avatar")}
          canBecomeSeller={
            profile.seller_status === "none" &&
            profile.role !== "admin" &&
            profile.role !== "super_admin"
          }
        />

        <SellerReviewsList
          sellerId={profile.id}
          sellerName={profile.full_name ?? undefined}
        />
      </div>

      <ImageEditor
        open={editor === "cover"}
        onClose={() => setEditor(null)}
        title="Editar foto de portada"
        type="cover"
        currentUrl={profile.cover_image}
        userId={profile.id}
        onSave={persistCover}
      />
      <ImageEditor
        open={editor === "avatar"}
        onClose={() => setEditor(null)}
        title="Editar foto de perfil"
        type="avatar"
        currentUrl={profile.avatar_url}
        userId={profile.id}
        onSave={persistAvatar}
      />
    </div>
  );
}